import { Play, Download, Package, Trash2, FileVideo, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { VideoFile } from '../types';

interface VideoListCompactProps {
  videos: VideoFile[];
  isProcessing: boolean;
  onProcessVideo: (video: VideoFile) => void;
  onProcessAll: () => void;
  onDownloadVideo: (video: VideoFile) => void;
  onDownloadAll: () => void;
  onDownloadAllAsZip: () => void;
  onRemoveVideo: (id: string) => void;
  onClearAll: () => void;
  isDarkMode: boolean;
  settings: any;
}

export default function VideoListCompact({
  videos,
  isProcessing,
  onProcessVideo,
  onProcessAll,
  onDownloadVideo,
  onDownloadAll,
  onDownloadAllAsZip,
  onRemoveVideo,
  onClearAll,
  isDarkMode,
  settings
}: VideoListCompactProps) {
  if (videos.length === 0) {
    return null;
  }

  const pendingVideos = videos.filter(video => video.status === 'pending');
  const completedVideos = videos.filter(video => video.status === 'completed');
  const hasCompletedVideos = completedVideos.length > 0;

  const getStatusIcon = (status: VideoFile['status']) => {
    switch (status) {
      case 'pending':
        return <FileVideo className="w-4 h-4 text-blue-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProcessAll = () => {
    console.log('🚀 BOTÃO CONVERTER TODOS CLICADO! (VideoListCompact)');
    console.log('📊 Estado atual no VideoListCompact:', {
      totalVideos: videos.length,
      pendingVideos: pendingVideos.length,
      isProcessing,
      videos: videos.map(vid => ({
        id: vid.id,
        name: vid.file?.name,
        status: vid.status,
        hasFile: !!vid.file
      }))
    });
    onProcessAll();
  };

  return (
    <div className="w-full space-y-6">
      {/* Header com Controles */}
      <div className={`${
        isDarkMode ? 'bg-[#2A2E3F] border-[#4A4F64]' : 'bg-white border-[#E5E5E5]'
      } border rounded-xl p-3 space-y-3 md:space-y-0 md:flex md:items-center md:justify-between`}>
        {/* Título e Quantidade */}
        <div className="flex items-center gap-2">
          <h3 className={`text-xl font-medium ${
            isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
          }`}>
            {videos.length === 1 ? 'Vídeo Carregado' : 'Vídeos Carregados'}
          </h3>
          <span className="text-sm px-2 py-0.5 rounded-md bg-[#1f2937] text-[#60a5fa]">
            {videos.length} total
          </span>
        </div>
        
        {/* Botões */}
        <div className="flex items-center gap-2">
          {hasCompletedVideos && (
            <>
              <button
                onClick={onDownloadAll}
                className="hidden lg:flex flex-1 md:flex-initial items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md"
                title={`Baixar todos (${completedVideos.length})`}
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Baixar Todos</span>
                <span className="text-xs opacity-75">({completedVideos.length})</span>
              </button>

              <button
                onClick={onDownloadAllAsZip}
                className="hidden lg:flex flex-1 md:flex-initial items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md"
                title="Baixar como ZIP"
              >
                <Package className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>ZIP</span>
              </button>
            </>
          )}

          {pendingVideos.length > 0 && !hasCompletedVideos && (
            <button
              onClick={handleProcessAll}
              className="flex-1 md:flex-initial flex items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md"
            >
              <Play className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Converter Todos</span>
              <span className="hidden lg:inline text-xs opacity-75">({pendingVideos.length})</span>
            </button>
          )}
          
          <button
            onClick={onClearAll}
            className={`flex-1 md:flex-initial flex items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 ${
              isDarkMode ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300' : 'bg-red-100 hover:bg-red-200 text-red-600'
            } rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[36px] sm:min-h-[40px] touch-manipulation active:scale-95 hover:shadow-md`}
          >
            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Limpar Tudo</span>
          </button>
        </div>
      </div>

      {/* Lista de Vídeos */}
      <div className="space-y-3">
        {videos.map((video) => (
          <div
            key={video.id}
            className={`flex items-center justify-between p-3 sm:p-4 rounded-xl border transition-colors ${
              isDarkMode 
                ? 'bg-[#2A2E3F] border-[#4A4F64] hover:bg-[#383D52]' 
                : 'bg-white border-[#E5E5E5] hover:bg-[#F8F9FA]'
            }`}
          >
            {/* Info do Vídeo */}
            <div className="flex items-center space-x-3 flex-1 min-w-0 pr-3">
              {/* Preview */}
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                {video.preview ? (
                  <img
                    src={video.preview}
                    alt={video.file.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileVideo className="w-5 h-5 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Detalhes */}
              <div className="min-w-0 flex-1">
                {/* Nome do arquivo - apenas Desktop */}
                <div className={`hidden md:block text-sm font-medium mb-1 truncate ${
                  isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                }`} title={video.file.name}>
                  {video.file.name}
                </div>
                
                {(() => {
                  const currentFormat = video.status === 'completed' ? settings.format : video.file.name.split('.').pop();
                  return (
                    <div className={`text-xs sm:text-sm ${
                      isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                    } flex items-center space-x-2`}>
                      <span>{formatFileSize(video.file.size)}</span>
                      <span>•</span>
                      {video.originalDuration && <span>{formatDuration(video.originalDuration)}</span>}
                      <span>•</span>
                      <span className="uppercase font-medium">{currentFormat}</span>
                    </div>
                  );
                })()}

                {/* Status */}
                <div className="flex items-center space-x-1.5 mt-1">
                  {getStatusIcon(video.status)}
                  <span className={`text-xs ${
                    isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                  }`}>
                    {video.status === 'pending' && 'Pendente'}
                    {video.status === 'processing' && 'Processando...'}
                    {video.status === 'completed' && 'Concluído'}
                    {video.status === 'error' && 'Erro'}
                  </span>
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-1.5">
              {video.status === 'pending' && !isProcessing && (
                <button
                  onClick={() => onProcessVideo(video)}
                  className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs font-medium transition-all duration-300"
                >
                  <Play className="w-3.5 h-3.5" />
                  <span>Converter</span>
                </button>
              )}

              {video.status === 'completed' && (
                <button
                  onClick={() => onDownloadVideo(video)}
                  className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition-all duration-300"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Baixar</span>
                </button>
              )}

              <button
                onClick={() => onRemoveVideo(video.id)}
                className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' 
                    : 'bg-red-100 hover:bg-red-200 text-red-600'
                }`}
                title="Remover"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}